import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { loginSchema, type LoginInput } from '@pandacalcio/validation';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../features/auth/AuthContext';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { usePageTitle } from '../../lib/usePageTitle';
import { AuthLayout } from '../../components/AuthLayout';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';
import type { PublicUser } from '../../features/auth/types';

export function LoginPage() {
  usePageTitle('Accedi');
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const { announce } = useAnnouncer();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  const onSubmit = handleSubmit(async (input) => {
    setServerError(null);
    try {
      const res = await api<{ user: PublicUser }>('/auth/login', { method: 'POST', body: input });
      setUser(res.user);
      announce(`Accesso effettuato. Benvenuto, ${res.user.displayName}.`);
      const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Qualcosa è andato storto. Riprova.';
      setServerError(message);
      announce(`Errore: ${message}`, 'assertive');
    }
  });

  const from = (location.state as { from?: string } | null)?.from;

  return (
    <AuthLayout title="Accedi">
      {from && (
        <p
          role="status"
          className="mb-4 rounded-lg border border-panda-300 bg-panda-50 p-3 dark:border-panda-700 dark:bg-ink-800"
        >
          Per continuare devi prima accedere
          {from === '/entra-con-codice' ? ' (o registrarti): poi potrai usare il codice invito.' : ': poi ti riportiamo dove volevi andare.'}{' '}
          Non hai un account?{' '}
          <Link to="/registrati" className="font-semibold text-panda-700 underline dark:text-panda-300">
            Registrati
          </Link>
        </p>
      )}
      {serverError && (
        <div
          role="alert"
          className="mb-4 rounded-lg border-2 border-danger bg-red-50 p-3 font-medium text-danger dark:bg-ink-800"
        >
          {serverError}
        </div>
      )}

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          required
          error={errors.email?.message}
          {...register('email')}
        />
        <TextField
          label="Password"
          type="password"
          revealable
          autoComplete="current-password"
          required
          error={errors.password?.message}
          {...register('password')}
        />
        <Button type="submit" loading={isSubmitting} loadingText="Accesso in corso…">
          Accedi
        </Button>
      </form>

      <nav aria-label="Altre opzioni di accesso" className="mt-6 flex flex-col gap-2 text-sm">
        <Link to="/password-dimenticata" className="text-panda-700 underline dark:text-panda-300">
          Password dimenticata?
        </Link>
        <p>
          Non hai un account?{' '}
          <Link to="/registrati" className="text-panda-700 underline dark:text-panda-300">
            Registrati
          </Link>
        </p>
      </nav>
    </AuthLayout>
  );
}
